import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, ShieldCheck, RotateCcw, Globe, ChevronLeft, ChevronRight } from 'lucide-react';
import { products, heroSlides, medinaImage } from '../data/products';
import ProductCard from '../components/ProductCard';
import './Home.css';

function Home({ addToCart }) {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const featuredProducts = products.slice(0, 4);
  const bestSellers = products.filter(p => p.badge === 'BESTSELLER' || p.badge === 'POPULAR' || p.rating >= 4.8).slice(0, 4);
  const onSaleProducts = products.filter(p => p.oldPrice).slice(0, 4);

  const categoryCards = [
    { id: 'food', name: 'Food & Dates', icon: '🌴', count: products.filter(p => p.category === 'food').length, color: 'linear-gradient(135deg, #1a3a1a, #0f1f0f)' },
    { id: 'clothing', name: 'Clothing', icon: '👗', count: products.filter(p => p.category === 'clothing').length, color: 'linear-gradient(135deg, #3a1a1a, #1f0f0f)' },
    { id: 'accessories', name: 'Accessories', icon: '📿', count: products.filter(p => p.category === 'accessories').length, color: 'linear-gradient(135deg, #1a1a3a, #0f0f1f)' },
    { id: 'fragrance', name: 'Fragrance', icon: '💐', count: products.filter(p => p.category === 'fragrance').length, color: 'linear-gradient(135deg, #3a2a1a, #1f150f)' },
    { id: 'beauty', name: 'Beauty', icon: '🎨', count: products.filter(p => p.category === 'beauty').length, color: 'linear-gradient(135deg, #2a1a3a, #150f1f)' },
  ];

  return (
    <div className="home">
      {/* Hero Slider */}
      <section className="hero-slider">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
            style={{
              backgroundImage: `linear-gradient(rgba(15,15,15,0.7), rgba(15,15,15,0.5)), url(${slide.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="hero-slide-content">
              <span className="hero-eyebrow">✦ {slide.subtitle} ✦</span>
              <h1 className="hero-title">{slide.title}</h1>
              <p className="hero-desc">{slide.description}</p>
              <div className="hero-actions">
                <Link to={slide.link} className="btn btn-gold hero-cta">
                  {slide.cta} <ArrowRight size={18} />
                </Link>
                <Link to="/about" className="btn hero-cta-secondary">
                  Our Story
                </Link>
              </div>
            </div>
          </div>
        ))}

        <button className="slider-arrow slider-prev" onClick={prevSlide}>
          <ChevronLeft size={24} />
        </button>
        <button className="slider-arrow slider-next" onClick={nextSlide}>
          <ChevronRight size={24} />
        </button>

        <div className="slider-dots">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => setCurrentSlide(index)}
            />
          ))}
        </div>
      </section>

      {/* Trust Bar */}
      <section className="trust-bar">
        <div className="trust-container">
          <div className="trust-item">
            <Truck size={28} />
            <div>
              <h4>Free Shipping</h4>
              <p>On orders over 500 SAR</p>
            </div>
          </div>
          <div className="trust-item">
            <ShieldCheck size={28} />
            <div>
              <h4>Secure Payment</h4>
              <p>100% protected checkout</p>
            </div>
          </div>
          <div className="trust-item">
            <RotateCcw size={28} />
            <div>
              <h4>30-Day Returns</h4>
              <p>Easy return policy</p>
            </div>
          </div>
          <div className="trust-item">
            <Globe size={28} />
            <div>
              <h4>Global Shipping</h4>
              <p>100+ countries worldwide</p>
            </div>
          </div>
        </div>
      </section>

      {/* Shop by Category */}
      <section className="categories-section">
        <div className="section-header">
          <h2 className="section-title">Shop by Category</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">Explore our curated collections</p>
        </div>

        <div className="categories-grid">
          {categoryCards.map(cat => (
            <Link
              key={cat.id}
              to="/collection"
              className="category-card"
              style={{ background: cat.color }}
            >
              <div className="category-icon">{cat.icon}</div>
              <h3>{cat.name}</h3>
              <span className="category-count">{cat.count} products</span>
              <div className="category-arrow">
                <ArrowRight size={20} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      <section className="featured-section">
        <div className="section-header">
          <h2 className="section-title">Featured Products</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">Handpicked essentials just for you</p>
        </div>

        <div className="products-grid">
          {featuredProducts.map(product => (
            <ProductCard key={product.id} product={product} addToCart={addToCart} />
          ))}
        </div>

        <div className="view-all-container">
          <Link to="/collection" className="btn view-all-btn">
            View All Products <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Promo Banner */}
      <section className="promo-banner">
        <div className="promo-content">
          <span className="promo-tag">SPECIAL OFFER</span>
          <h2>Up to 25% Off Selected Items</h2>
          <p>Limited time offer on premium halal essentials from Medina</p>
          <Link to="/collection" className="btn btn-gold">
            Shop Sale <ArrowRight size={18} />
          </Link>
        </div>
        <div className="promo-decoration">
          <div className="promo-circle promo-circle-1"></div>
          <div className="promo-circle promo-circle-2"></div>
          <div className="promo-circle promo-circle-3"></div>
        </div>
      </section>

      {/* Best Sellers */}
      <section className="bestsellers-section">
        <div className="section-header">
          <h2 className="section-title">Best Sellers</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">Our most loved products by customers</p>
        </div>

        <div className="products-grid">
          {bestSellers.map(product => (
            <ProductCard key={product.id} product={product} addToCart={addToCart} />
          ))}
        </div>
      </section>

      {/* On Sale */}
      {onSaleProducts.length > 0 && (
        <section className="sale-section">
          <div className="section-header">
            <h2 className="section-title">On Sale</h2>
            <div className="section-divider"></div>
            <p className="section-subtitle">Don't miss out on these great deals</p>
          </div>

          <div className="products-grid">
            {onSaleProducts.map(product => (
              <ProductCard key={product.id} product={product} addToCart={addToCart} />
            ))}
          </div>
        </section>
      )}

      {/* Brand Story Teaser */}
      <section className="brand-teaser">
        <div className="brand-teaser-content">
          <span className="teaser-eyebrow">OUR STORY</span>
          <h2>From Medina to the World</h2>
          <p>
            Founded in 2026 in the holy city of Medina, Bayt Al-Khayr brings the finest
            halal-certified essentials to homes around the world. Rooted in tradition
            and crafted for the modern era, we source premium, sustainably made products
            that uphold quality, integrity, and compassion.
          </p>
          <div className="teaser-stats">
            <div className="teaser-stat">
              <h3>100+</h3>
              <p>Countries Served</p>
            </div>
            <div className="teaser-stat">
              <h3>50K+</h3>
              <p>Happy Customers</p>
            </div>
            <div className="teaser-stat">
              <h3>100%</h3>
              <p>Halal Certified</p>
            </div>
          </div>
          <Link to="/about" className="btn">
            Learn More About Us <ArrowRight size={18} />
          </Link>
        </div>
        <div className="brand-teaser-image">
          <div className="teaser-image-frame">
            <img 
              src={medinaImage} 
              alt="Medina, Saudi Arabia" 
              className="teaser-medina-img" 
            />
          </div>
          <div className="teaser-image-accent"></div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="newsletter-section">
        <div className="newsletter-content">
          <h2>Stay Connected</h2>
          <p>Subscribe to receive updates on new products, special offers, and more.</p>
          <form className="newsletter-form" onSubmit={(e) => { e.preventDefault(); alert('Thank you for subscribing!'); }}>
            <input type="email" placeholder="Enter your email address" required />
            <button type="submit" className="btn btn-gold">
              Subscribe
            </button>
          </form>
          <p className="newsletter-note">We respect your privacy. Unsubscribe at any time.</p>
        </div>
      </section>
    </div>
  );
}

export default Home;
